/**
 * The DOM overlay (v0.2 RFC §8): HUD strip · contextual action bar · tab bar
 * (tabs enable as tiers open) · More sheet (Sober Living, Alumni, New Game) ·
 * banners · fatal panel. textContent-only. Mounted once by main.ts.
 */

import {
  ALUMNI,
  AUTOMATOR_ACTIONS_BY_LEVEL,
  OFFICE,
  type CareTierId,
  type DischargePolicy,
} from "@/data/tunables";
import { netPerSec } from "@/systems/engine";
import { runtime } from "@/systems/runtime";
import {
  automatorUpgradeCost,
  contactCall,
  coordinatorUpgradeCost,
  purchaseTier,
  setFacilityName,
  recruiterUpgradeCost,
  setDischargePolicy,
  upgradeAutomator,
  upgradeCoordinator,
  upgradeRecruiter,
  recruit,
  admitFromQueue,
  prFirmUpgradeCost,
  upgradePrFirm,
  complianceUpgradeCost,
  upgradeComplianceOfficer,
  fixerUpgradeCost,
  upgradeFixer,
} from "@/systems/actions";
import { frontierTier, type TierId } from "@/state/GameState";
import { clearSave, saveGame } from "@/saves/SaveManager";
import { letterById, readLetter, unreadCount } from "@/systems/mailbox";
import { prestige, prestigePayout, purchaseUpgrade, shopEntries } from "@/systems/prestige";
import { deaDanger, heatDanger, type GameOverReason } from "@/systems/meters";
import { CRIMINAL_TIER_TITLES, CRIMINAL_UPGRADES, criminalUpgradeCostAt } from "@/data/criminal";
import { buyCriminalUpgrade, criminalIncomePerTick } from "@/systems/criminal";
import { upgradeDef } from "@/data/upgrades";
import { DEFAULT_FACILITY_NAME, METERS, TAGLINES } from "@/data/tunables";
import { GAME, HERO } from "@/config";
import { sendFeedback, FEEDBACK_TEXT_MAX, type FeedbackCategory } from "@/systems/feedback";
import { _telemetryEnabled as telemetryEnabled } from "@/systems/telemetry";
import { PALETTE_HEX } from "@/palette";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** Public-asset URL (mirrors PreloadScene.assetUrl) for DOM background-images. */
const assetUrl = (relToPublic: string): string => `${import.meta.env.BASE_URL}${relToPublic}`;

function root(): HTMLElement {
  const r = document.getElementById("ui-overlay");
  if (r === null) {
    throw new Error('mountOverlay: #ui-overlay not found in the document.');
  }
  return r;
}

/** Scene keys the tab bar / More sheet can route to. */
export type SceneKey =
  | "office"
  | "tier-detox"
  | "tier-residential"
  | "tier-php"
  | "tier-iop"
  | "tier-soberLiving"
  | "alumni";

export interface ActionSpec {
  label: () => string;
  disabled?: () => boolean;
  /** TIME-based cooldowns only: fraction (0..1) of the cooldown still remaining.
   *  Drives a clockwise radial wipe on the tile; 0/undefined = no timer (a
   *  state-based disable, e.g. can't-afford, just dims). */
  cooldown?: () => number;
  secondary?: boolean;
  /** Tile graphic — asset path relative to public/ (transparent icon; the tile
   *  background stays a CSS colour). */
  icon?: string;
  onTap: () => void;
}

export interface OverlayHandles {
  setActions(actions: ActionSpec[]): void;
  setActiveTab(key: SceneKey): void;
  refresh(): void;
  openMoreSheet(): void;
  /** Re-sync the office hero's edge wings to the current canvas rect (resize). */
  relayoutHero(): void;
}

interface ContinuumStep {
  scene: SceneKey;
  label: string;
  /** Which tier must be unlocked for this step (null = always available). */
  requiresTier: TierId | null;
}

/** The full care continuum, in order — the edge arrows page through the UNLOCKED
 *  steps (Sober Living + Alumni included, so every scene has arrow navigation). */
const CONTINUUM: ContinuumStep[] = [
  { scene: "office", label: "Office", requiresTier: null },
  { scene: "tier-detox", label: "Detox", requiresTier: "detox" },
  { scene: "tier-residential", label: "Residential", requiresTier: "residential" },
  { scene: "tier-php", label: "PHP", requiresTier: "php" },
  { scene: "tier-iop", label: "IOP", requiresTier: "iop" },
  { scene: "tier-soberLiving", label: "Sober Living", requiresTier: "soberLiving" },
  { scene: "alumni", label: "Alumni", requiresTier: "alumni" },
];

/** Band colour per scene — MIRRORS HERO_THEME.band in scenes/hero.ts (the DOM
 *  wings can't read the canvas, so the mapping is duplicated; keep in sync). */
const BAND_HEX: Record<string, string> = {
  office: PALETTE_HEX.teal,
  "tier-detox": PALETTE_HEX.coralRed,
  "tier-residential": PALETTE_HEX.green,
  "tier-php": PALETTE_HEX.mustard,
  "tier-iop": PALETTE_HEX.teal,
  "tier-soberLiving": PALETTE_HEX.coralRed,
  alumni: PALETTE_HEX.green,
};

export function mountOverlay(onScene: (key: SceneKey) => void): OverlayHandles {
  const host = root();

  // ── HUD ──
  const hud = el("div", "hud");
  const funding = el("div", "funding");
  const patients = el("div", "stat");
  const net = el("div", "stat");
  const rep = el("div", "stat");
  const repCap = el("div", "stat repcap"); // Reputation Capital — shown once > 0
  const meters = el("div", "meters");
  const heatBar = el("i");
  const deaBar = el("i");
  const heatMeter = el("div", "meter");
  heatMeter.appendChild(heatBar);
  const deaMeter = el("div", "meter");
  deaMeter.appendChild(deaBar);
  meters.append(heatMeter, deaMeter);
  // Mail lives OUTSIDE the HUD now (floats below it) so the HUD strip stays slim.
  const mailBtn = el("button", "mail", "✉");
  mailBtn.type = "button";
  mailBtn.addEventListener("click", () => openMailSheet(onScene, handles));
  hud.append(funding, patients, net, rep, repCap, meters);

  // ── office hero "wings" — DOM extensions of the in-canvas hero band that
  //    continue its teal fill + charcoal top/bottom rules out to the true
  //    screen edges on any screen wider than the FIT-scaled canvas (desktop /
  //    non-Pro-Max aspect). On the Pro Max the canvas fills the width, so both
  //    wings compute to zero width and hide. Geometry is synced to the live
  //    canvas rect (see layoutHeroWings); only shown while the office is active.
  const leftWing = el("div", "hero-wing");
  const rightWing = el("div", "hero-wing");
  leftWing.style.display = "none";
  rightWing.style.display = "none";

  // ── tier pager arrows (replace the tab row): a small 50%-opacity box with a
  //    chevron on top and the destination label below. They live OUTSIDE the
  //    canvas, in the letterbox — positioned to the canvas L/R edges by the sync
  //    below so they never overlap or clip against the game.
  const arrowPrev = el("button", "tier-arrow prev");
  const arrowNext = el("button", "tier-arrow next");
  arrowPrev.type = "button";
  arrowNext.type = "button";
  const prevLabel = el("span", "tlabel");
  const nextLabel = el("span", "tlabel");
  arrowPrev.append(el("i", "chev"), prevLabel); // chevron on top, label below
  arrowNext.append(el("i", "chev"), nextLabel);

  let heroVisible = true; // office is the boot scene
  let currentBandColor: string = PALETTE_HEX.teal; // wing colour, matched to the active scene's band
  const layoutHeroWings = (): void => {
    const canvas = document.querySelector<HTMLCanvasElement>("#game-canvas canvas");
    if (canvas === null) {
      leftWing.style.display = "none";
      rightWing.style.display = "none";
      return;
    }
    const r = canvas.getBoundingClientRect();
    const scale = r.width / GAME.width; // Scale.FIT is uniform on both axes
    // Tier arrows sit in the letterbox just OUTSIDE the canvas, vertically centred.
    const midY = r.top + r.height / 2;
    arrowPrev.style.top = `${midY}px`;
    arrowNext.style.top = `${midY}px`;
    // Sit at the canvas L/R edges, but CLAMP so they stay on-screen when there's
    // no letterbox (Pro Max fullscreen: r.left=0, r.right=innerWidth) — otherwise
    // both arrows go off-screen and tier navigation is unreachable.
    const ARROW_EDGE = 34; // .tier-arrow width (30px) + 4px breathing room — keep the button fully on-screen
    arrowPrev.style.left = "auto";
    arrowPrev.style.right = `${Math.max(0, Math.min(window.innerWidth - r.left, window.innerWidth - ARROW_EDGE))}px`;
    arrowNext.style.right = "auto";
    arrowNext.style.left = `${Math.max(0, Math.min(r.right, window.innerWidth - ARROW_EDGE))}px`;
    // Mail button → lower-right corner of the hero band, tracked to the RIGHT
    // WING's outer edge (the band's visible extended corner), not the canvas edge.
    const mailInset = Math.round(16 * scale);
    const rightWingW = Math.min(Math.max(0, window.innerWidth - r.right), HERO.edgeSafe * scale);
    const bandRight = r.right + rightWingW;
    mailBtn.style.left = "auto";
    mailBtn.style.right = `${Math.max(4, window.innerWidth - bandRight + mailInset)}px`;
    mailBtn.style.top = `${r.top + (HERO.y + HERO.h) * scale - mailBtn.offsetHeight - mailInset}px`;
    if (!heroVisible) {
      leftWing.style.display = "none";
      rightWing.style.display = "none";
      return;
    }
    const top = r.top + HERO.y * scale;
    const height = HERO.h * scale;
    const rule = Math.max(1, HERO.border * scale);
    const cap = HERO.edgeSafe * scale; // extend AT MOST the safety zone past the canvas
    // Each wing butts the canvas edge (seamless inner join) and extends outward
    // up to `cap`, with a rounded, charcoal-outlined OUTER cap.
    const wings = [
      { node: leftWing, side: "left" as const, avail: r.left },
      { node: rightWing, side: "right" as const, avail: window.innerWidth - r.right },
    ];
    for (const { node: wing, side, avail } of wings) {
      const w = Math.min(avail, cap);
      if (w <= 0.5) {
        wing.style.display = "none";
        continue;
      }
      const radius = Math.min(HERO.radius * scale, w, height / 2);
      const left = side === "left";
      wing.style.display = "block";
      wing.style.backgroundColor = currentBandColor; // match the scene's band
      wing.style.top = `${top}px`;
      wing.style.height = `${height}px`;
      wing.style.left = `${left ? r.left - w : r.right}px`;
      wing.style.width = `${w}px`;
      wing.style.borderTopWidth = `${rule}px`;
      wing.style.borderBottomWidth = `${rule}px`;
      wing.style.borderLeftWidth = left ? `${rule}px` : "0px";
      wing.style.borderRightWidth = left ? "0px" : `${rule}px`;
      wing.style.borderTopLeftRadius = left ? `${radius}px` : "0px";
      wing.style.borderBottomLeftRadius = left ? `${radius}px` : "0px";
      wing.style.borderTopRightRadius = left ? "0px" : `${radius}px`;
      wing.style.borderBottomRightRadius = left ? "0px" : `${radius}px`;
    }
  };

  // ── bottom cluster ──
  const bottom = el("div", "bottom");
  const actions = el("div", "actions");
  // "Menu" — a square icon on the right that opens the sheet (Sober Living /
  // Alumni / New Game). Tier navigation is the edge arrows.
  const menuBtn = el("button", "menu-btn");
  menuBtn.type = "button";
  menuBtn.append(el("span", "menu-glyph", "☰"), el("span", "action-cap", "Menu"));
  menuBtn.addEventListener("click", () => handles.openMoreSheet());
  bottom.append(actions, menuBtn);

  // ── tier pager arrows: prev = LEFT edge, next = RIGHT edge (both vertically
  //    centered), navigating ONLY unlocked tiers along the continuum. ──
  let currentSceneKey: SceneKey = "office";
  const unlockedSeq = (): ContinuumStep[] => {
    const open = runtime.state.run.availableTiers;
    return CONTINUUM.filter((d) => d.requiresTier === null || open.includes(d.requiresTier));
  };
  // The tier NAME is a transient popover: shown by revealLabel (auto-hides after
  // `ms`, or stays until hideLabel when ms is 0 — used by long-press).
  const revealTimer = new Map<HTMLElement, number>();
  const revealLabel = (arrow: HTMLElement, ms: number) => {
    arrow.classList.remove("reveal");
    void arrow.offsetWidth; // restart the pop animation if already shown
    arrow.classList.add("reveal");
    const t = revealTimer.get(arrow);
    if (t) window.clearTimeout(t);
    if (ms > 0) revealTimer.set(arrow, window.setTimeout(() => arrow.classList.remove("reveal"), ms));
  };
  const hideLabel = (arrow: HTMLElement) => {
    const t = revealTimer.get(arrow);
    if (t) window.clearTimeout(t);
    arrow.classList.remove("reveal");
  };
  let seenTiers: Set<string> | null = null; // null until first refresh (no reveal on load)
  const refreshArrows = () => {
    const open = runtime.state.run.availableTiers;
    const seq = unlockedSeq();
    const idx = seq.findIndex((d) => d.scene === currentSceneKey);
    const prev = idx > 0 ? seq[idx - 1] : undefined;
    const next = idx >= 0 && idx < seq.length - 1 ? seq[idx + 1] : undefined;
    arrowPrev.classList.toggle("hidden", !prev);
    arrowNext.classList.toggle("hidden", !next);
    if (prev) prevLabel.textContent = prev.label;
    if (next) nextLabel.textContent = next.label;
    // Teaching moment: when a tier newly unlocks (its letter fires), animate the
    // name on the arrow that now leads to it.
    if (seenTiers === null) {
      seenTiers = new Set(open);
    } else {
      // A run reset (prestige resets run in-place, no reload) shrinks
      // availableTiers below what we've seen — re-baseline so the next run's
      // unlocks animate again.
      for (const t of seenTiers) {
        if (!open.includes(t as TierId)) {
          seenTiers = new Set(open);
          break;
        }
      }
      for (const tier of open) {
        if (seenTiers.has(tier)) continue;
        seenTiers.add(tier);
        if (next?.requiresTier === tier) revealLabel(arrowNext, 4500);
        else if (prev?.requiresTier === tier) revealLabel(arrowPrev, 4500);
      }
    }
  };
  const stepTier = (delta: number) => {
    const seq = unlockedSeq();
    const idx = seq.findIndex((d) => d.scene === currentSceneKey);
    const target = idx >= 0 ? seq[idx + delta] : undefined;
    if (target) onScene(target.scene as SceneKey);
  };
  // Long-press = the touch "tooltip": peek the destination name; the peek
  // suppresses the navigation tap that would otherwise fire on release.
  const wireLongPress = (arrow: HTMLButtonElement) => {
    let holdT = 0;
    let peeked = false;
    arrow.addEventListener("pointerdown", () => {
      peeked = false;
      holdT = window.setTimeout(() => {
        peeked = true;
        revealLabel(arrow, 0);
      }, 400);
    });
    const release = () => {
      window.clearTimeout(holdT);
      if (peeked) window.setTimeout(() => hideLabel(arrow), 1100);
    };
    arrow.addEventListener("pointerup", release);
    arrow.addEventListener("pointercancel", release);
    arrow.addEventListener("pointerleave", release);
    arrow.addEventListener(
      "click",
      (e) => {
        if (peeked) {
          e.stopImmediatePropagation();
          e.preventDefault();
          peeked = false;
        }
      },
      true,
    );
  };
  // wireLongPress FIRST: its click suppressor must be registered before the nav
  // handler so it wins at the AT_TARGET phase (listeners fire in registration
  // order there, ignoring capture/bubble) — otherwise a long-press on the button's
  // own area still navigates on release.
  wireLongPress(arrowPrev);
  wireLongPress(arrowNext);
  // Navigating dismisses the teaching label — once you've used it, you've learnt it.
  arrowPrev.addEventListener("click", () => {
    hideLabel(arrowPrev);
    stepTier(-1);
  });
  arrowNext.addEventListener("click", () => {
    hideLabel(arrowNext);
    stepTier(1);
  });

  host.append(leftWing, rightWing, hud, mailBtn, arrowPrev, arrowNext, bottom);

  let current: ActionSpec[] = [];
  const actionButtons: HTMLButtonElement[] = [];

  const refreshTabs = () => {
    // Tier nav is the edge arrows; the Menu button is static.
    refreshArrows();
  };

  const refresh = () => {
    const state = runtime.state;
    if (!state) return;
    funding.textContent = `$${Math.floor(state.run.funding).toLocaleString()}`;
    patients.textContent = "";
    patients.append(el("b", undefined, `${state.run.patients.length}`), "patients");
    net.textContent = "";
    net.append(el("b", undefined, `$${netPerSec(state).toFixed(1)}/s`), "net");
    rep.textContent = "";
    rep.append(el("b", undefined, `${state.run.reputation}`), "rep");
    const rc = Math.floor(state.account.reputationCapital);
    repCap.classList.toggle("hidden", rc <= 0);
    if (rc > 0) {
      repCap.textContent = "";
      repCap.append(el("b", undefined, `${rc.toLocaleString()}`), "RC");
    }
    heatBar.style.width = `${Math.min(100, state.run.heat)}%`;
    deaBar.style.width = `${Math.min(100, state.run.dea)}%`;
    heatMeter.classList.toggle("danger", heatDanger(state)); // v0.5 game-over tell
    // hide the DEA meter until the criminal track exists — its presence would
    // otherwise spoil the hidden reveal (v0.6 review).
    deaMeter.classList.toggle("hidden", !state.run.criminal.unlocked);
    deaMeter.classList.toggle("danger", deaDanger(state)); // v0.6 raid tell (shared helper)
    const unread = unreadCount(state);
    mailBtn.textContent = unread > 0 ? `✉ ${unread}` : "✉";
    mailBtn.classList.toggle("has-mail", unread > 0);
    current.forEach((spec, i) => {
      const b = actionButtons[i]!;
      const cap = b.querySelector<HTMLElement>(".action-cap");
      if (cap) cap.textContent = spec.label();
      b.disabled = spec.disabled?.() ?? false;
      // Clockwise radial wipe for time-based cooldowns; plain dim otherwise.
      const cd = spec.cooldown?.() ?? 0;
      const cool = b.querySelector<HTMLElement>(".action-cooldown");
      if (cool) {
        cool.style.display = cd > 0 ? "block" : "none";
        if (cd > 0) {
          const swept = (1 - cd) * 360;
          cool.style.background = `conic-gradient(transparent 0deg, transparent ${swept}deg, rgba(34, 31, 26, 0.72) ${swept}deg)`;
        }
      }
    });
    refreshTabs();
    applyPendingPulse(actionButtons);
  };

  const handles: OverlayHandles = {
    setActions(specs) {
      current = specs;
      actions.textContent = "";
      actionButtons.length = 0;
      for (const spec of specs) {
        const b = el("button", spec.secondary ? "secondary" : undefined);
        b.type = "button";
        // Square tile: graphic + overlaid caption + radial cooldown veil.
        const art = el("span", "action-art");
        if (spec.icon) {
          art.style.backgroundImage = `url("${assetUrl(spec.icon)}")`;
          art.style.backgroundColor = "transparent"; // drop the placeholder tint
        }
        b.append(art, el("span", "action-cap"), el("span", "action-cooldown"));
        b.addEventListener("click", () => {
          try {
            spec.onTap();
          } catch (err) {
            console.error("halfway: action failed", err);
          }
          refresh();
        });
        actions.appendChild(b);
        actionButtons.push(b);
      }
      refresh();
      applyPendingPulse(actionButtons);
    },
    setActiveTab(key) {
      // Every scene now has the hero band, so the edge wings always show —
      // coloured to match the active scene's band.
      heroVisible = true;
      currentSceneKey = key;
      currentBandColor = BAND_HEX[key] ?? PALETTE_HEX.teal;
      layoutHeroWings();
      refreshArrows();
      // Sober Living / Alumni are also reachable via Menu — keep it highlighted.
      const onMenu = key === "tier-soberLiving" || key === "alumni";
      menuBtn.classList.toggle("active", onMenu);
    },
    refresh,
    openMoreSheet() {
      openMoreSheet(onScene);
    },
    relayoutHero: layoutHeroWings,
  };

  window.setInterval(refresh, 300);
  window.addEventListener("resize", layoutHeroWings);
  window.visualViewport?.addEventListener("resize", layoutHeroWings);
  // The canvas sizes in a couple of async passes on iOS; catch up (mirrors the
  // refit timeouts in main.ts).
  window.setTimeout(layoutHeroWings, 60);
  window.setTimeout(layoutHeroWings, 400);
  window.setTimeout(layoutHeroWings, 1600);
  return handles;
}

// ── the More sheet ──────────────────────────────────────────────────────────

function openMoreSheet(onScene: (key: SceneKey) => void): void {
  const host = root();
  if (host.querySelector(".sheet")) return;
  const sheet = el("div", "sheet");
  const card = el("div", "sheet-card");
  card.appendChild(el("h2", undefined, "More"));

  // Sober Living + Alumni are the edge-arrow scenes now — not listed here.

  // Mitigation staff (v0.5): PR Firm bends Reputation, Compliance Officer bends
  // Heat. Leveled, funding-bought; the sheet re-opens after a buy to show the
  // new level.
  const mitigation = (label: string, level: number, cost: number, atMax: boolean, buy: () => void): void => {
    const text = atMax
      ? `${label} — MAX (Lv${level})`
      : level === 0
        ? `Hire ${label} — $${cost.toLocaleString()}`
        : `${label} Lv${level + 1} — $${cost.toLocaleString()}`;
    const b = el("button", "secondary", text);
    b.type = "button";
    b.disabled = atMax || runtime.state.run.funding < cost;
    b.addEventListener("click", () => {
      try {
        buy();
      } catch (err) {
        console.error("halfway: mitigation upgrade failed", err);
      }
      sheet.remove();
      openMoreSheet(onScene); // re-open to reflect the new level/funding
    });
    card.appendChild(b);
  };
  {
    const pr = runtime.state.run.prFirmLevel;
    mitigation("PR Firm", pr, prFirmUpgradeCost(runtime.state), pr >= METERS.mitigationMaxLevel, () =>
      upgradePrFirm(runtime.state),
    );
    const co = runtime.state.run.complianceOfficerLevel;
    mitigation("Compliance Officer", co, complianceUpgradeCost(runtime.state), co >= METERS.mitigationMaxLevel, () =>
      upgradeComplianceOfficer(runtime.state),
    );
    // The Fixer bends DEA — only relevant once the criminal track (and its
    // federal heat) is in play, so it appears with the ledger (v0.15).
    if (runtime.state.run.criminal.unlocked) {
      const fx = runtime.state.run.fixerLevel;
      mitigation("The Fixer", fx, fixerUpgradeCost(runtime.state), fx >= METERS.mitigationMaxLevel, () =>
        upgradeFixer(runtime.state),
      );
    }
  }

  // Criminal ledger (v0.6): the route appears once the track is unlocked.
  if (runtime.state.run.criminal.unlocked) {
    const crim = el("button", "criminal", "📓 The Other Business");
    crim.type = "button";
    crim.addEventListener("click", () => {
      sheet.remove();
      openCriminalLedger();
    });
    card.appendChild(crim);
  }

  // Prestige (v0.4): the route appears once the "Sell the Facility" letter is read.
  if (runtime.state.run.prestigeOffered) {
    const sell = el("button", "prestige", "💼 Sell the Facility");
    sell.type = "button";
    sell.addEventListener("click", () => {
      sheet.remove();
      openPrestigeSheet();
    });
    card.appendChild(sell);
  }

  // v0.12 The Grievance Box: the playtest return channel. In-fiction skin,
  // plain-speech subtitle (LEGIBILITY LAW — a feedback form that reads as a
  // gag goes unused).
  const grievance = el("button", "secondary", "📋 File a Grievance — report a bug or suggest a joke");
  grievance.type = "button";
  grievance.addEventListener("click", () => {
    sheet.remove();
    openGrievanceSheet();
  });
  card.appendChild(grievance);

  const newGame = el("button", "danger", "New Game (erase save)");
  newGame.type = "button";
  newGame.addEventListener("click", () => {
    if (window.confirm("Erase your save and start over? This cannot be undone.")) {
      // Halt the runtime FIRST: reload() fires visibilitychange→hidden, and
      // the tab-hide save would write the live state back over the wipe.
      runtime.stop();
      clearSave(); // the one sanctioned wipe path
      window.location.reload();
    }
  });
  card.appendChild(newGame);

  card.appendChild(el("div", "sheet-version", `halfway v${__APP_VERSION__}`));
  // The disclosure keys on PROD, not the ntfy env — umami runs
  // independently of the game-telemetry flag (bot leg: a prod build
  // without ntfy still analyzes usage and must still say so).
  if (import.meta.env.PROD || telemetryEnabled()) {
    card.appendChild(el("div", "sheet-version", "playtest build: sends anonymous usage, error + progress reports"));
  }
  // v0.12.1: iOS Safari evicts site data after ~7 days away unless the
  // site is installed — tell the affected players the escape hatch,
  // honestly, right next to the version line.
  // iPadOS 13+ presents a macOS UA — the touch-points check catches it (bot leg).
  const iosDevice = /iPhone|iPad/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const iosSafari = iosDevice && !("standalone" in navigator && (navigator as { standalone?: boolean }).standalone === true);
  if (iosSafari) {
    card.appendChild(el("div", "sheet-version", "iPhone tip: Add to Home Screen protects your save — Safari can clear website data after ~7 days away"));
  }

  const close = el("button", undefined, "Close");
  close.type = "button";
  close.addEventListener("click", () => sheet.remove());
  card.appendChild(close);

  sheet.appendChild(card);
  sheet.addEventListener("click", (e) => {
    if (e.target === sheet) sheet.remove();
  });
  // Escape closes too (keyboard path); listener dies with the sheet.
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") sheet.remove();
  };
  document.addEventListener("keydown", onKey);
  const obs = new MutationObserver(() => {
    if (!sheet.isConnected) {
      document.removeEventListener("keydown", onKey);
      obs.disconnect();
    }
  });
  obs.observe(host, { childList: true });
  host.appendChild(sheet);
}

// ── the prestige screen (v0.4) ──────────────────────────────────────────────

/**
 * The prestige sheet: projected payout + the special-upgrade shop + the sell
 * action. Selling banks Reputation Capital and resets the run underneath, then
 * FREEZES the runtime so the player can spend the fresh capital before a full
 * reload swaps the scenes into the new facility. There is deliberately NO
 * backdrop-dismiss once sold — the only way forward is REOPEN (reload), so the
 * UI can never strand the player looking at the pre-sale scene.
 */
function openPrestigeSheet(): void {
  const host = root();
  if (host.querySelector(".sheet")) return;
  const sheet = el("div", "sheet");
  const card = el("div", "sheet-card prestige-sheet");
  let soldPayout = 0;

  // Shop lives in the PRE-SALE screen on purpose: perks apply through
  // prestige()→newRun(), which runs AT sell — so anything bought here (incl.
  // head-start) shapes the reopened facility. A perk bought "after" the sell
  // couldn't retroactively rebuild the already-reset run, so post-sale is
  // confirmation-only; this sale's payout funds the NEXT prestige's shop.
  const renderPreSale = (): void => {
    card.textContent = "";
    const state = runtime.state;
    card.appendChild(el("h2", undefined, "Sell the Facility"));
    card.appendChild(
      el(
        "p",
        undefined,
        "A private-equity fund acquires the facility. You keep your reputation and your Reputation Capital; the run ends and reopens fresh — every tier you've reached, plus one new frontier. Spend Reputation Capital below FIRST — upgrades shape the facility you reopen.",
      ),
    );
    const payout = prestigePayout(state);
    const line = el("div", "prestige-payout");
    line.append(el("b", undefined, `+${payout.toLocaleString()} RC`), " projected payout");
    card.appendChild(line);
    const skBalance = Math.floor(state.account.streetKnowledge);
    card.appendChild(
      el(
        "div",
        "prestige-balance",
        `Reputation Capital: ${Math.floor(state.account.reputationCapital).toLocaleString()}` +
          (skBalance > 0 ? `   ·   Street Knowledge: ${skBalance.toLocaleString()}` : ""),
      ),
    );

    card.appendChild(el("h3", undefined, "Permanent Upgrades"));
    for (const entry of shopEntries(state)) {
      const def = upgradeDef(entry.id);
      const row = el("div", "shop-row");
      row.appendChild(el("div", "shop-title", `${def.title} — Lv${entry.level}/${def.maxLevel}`));
      row.appendChild(el("div", "shop-blurb", def.blurb));
      const cur = entry.currency === "streetKnowledge" ? "SK" : "RC";
      const buy = el("button", "shop-buy", entry.maxed ? "MAX" : `Buy · ${entry.nextCost!.toLocaleString()} ${cur}`);
      buy.type = "button";
      buy.disabled = entry.maxed || !entry.affordable;
      buy.addEventListener("click", () => {
        // Snapshot the mutated account fields so a failed PERSIST doesn't leave
        // an unsaved purchase in memory. BOTH currencies are snapshotted — an
        // upgrade may be priced in Reputation Capital OR Street Knowledge (v0.6),
        // and purchaseUpgrade decrements whichever applies.
        const acct = runtime.state.account;
        const rcBefore = acct.reputationCapital;
        const skBefore = acct.streetKnowledge;
        const levelBefore = acct.upgrades[entry.id];
        try {
          purchaseUpgrade(runtime.state, entry.id);
        } catch (err) {
          console.error("halfway: upgrade purchase failed", err);
          return;
        }
        try {
          saveGame(runtime.state); // a permanent account purchase — persist immediately
        } catch (err) {
          console.error("halfway: upgrade purchase not persisted — rolling back", err);
          acct.reputationCapital = rcBefore;
          acct.streetKnowledge = skBefore;
          if (levelBefore === undefined) delete acct.upgrades[entry.id];
          else acct.upgrades[entry.id] = levelBefore;
          showSaveWarning();
        }
        renderPreSale();
      });
      row.appendChild(buy);
      card.appendChild(row);
    }

    const sell = el("button", "danger", "SELL — this ends the run");
    sell.type = "button";
    sell.addEventListener("click", () => {
      if (!window.confirm("Sell the facility? The current run ends and cannot be recovered.")) return;
      let result: { payout: number };
      try {
        result = prestige(runtime.state); // ATOMIC commit: banks RC + resets the run
      } catch (err) {
        console.error("halfway: prestige failed", err); // nothing committed — stay pre-sale
        return;
      }
      // Committed. Always advance to the sold screen even if persistence fails —
      // the reset already happened and can't be undone; surface save failure
      // loudly rather than stranding on a dead SELL button.
      soldPayout = result.payout;
      runtime.stop(); // freeze the fresh run until REOPEN reloads
      try {
        saveGame(runtime.state);
      } catch (err) {
        console.error("halfway: post-prestige save failed", err);
        showSaveWarning();
      }
      renderSold();
    });
    card.appendChild(sell);
    const close = el("button", undefined, "Not yet");
    close.type = "button";
    close.addEventListener("click", () => sheet.remove());
    card.appendChild(close);
  };

  const renderSold = (): void => {
    card.textContent = "";
    card.appendChild(el("h2", undefined, "Facility Sold"));
    card.appendChild(
      el("p", undefined, "Signed, sealed, acquired. Your legacy of care is now a line item — and your slate is clean."),
    );
    const line = el("div", "prestige-payout");
    line.append(el("b", undefined, `+${soldPayout.toLocaleString()} RC`), " banked");
    card.appendChild(line);
    card.appendChild(
      el(
        "div",
        "prestige-balance",
        `Reputation Capital: ${Math.floor(runtime.state.account.reputationCapital).toLocaleString()}`,
      ),
    );
    const reopen = el("button", "prestige", "REOPEN FRESH FACILITY");
    reopen.type = "button";
    reopen.addEventListener("click", () => window.location.reload());
    card.appendChild(reopen);
  };

  renderPreSale();
  sheet.appendChild(card);
  host.appendChild(sheet);
}

// ── the criminal ledger (v0.6) ──────────────────────────────────────────────

/**
 * The criminal ledger (More tab, hidden until unlocked). Lists the 5 tiers +
 * their upgrades (locked / owned / affordable) with the live DEA reading and the
 * passive income the operation generates. Re-renders in place on each buy.
 */
function openCriminalLedger(): void {
  const host = root();
  if (host.querySelector(".sheet")) return;
  const sheet = el("div", "sheet");
  const card = el("div", "sheet-card criminal-ledger");

  const render = (): void => {
    card.textContent = "";
    const run = runtime.state.run;
    card.appendChild(el("h2", undefined, "The Other Business"));
    const dea = Math.floor(run.dea);
    const summary = el("div", "prestige-balance");
    summary.append(
      el("b", "dea-read", `DEA ${dea}/${METERS.deaCeiling}`),
      `  ·  crime +$${Math.floor(criminalIncomePerTick(runtime.state)).toLocaleString()}/tick`,
    );
    card.appendChild(summary);

    for (let tier = 1; tier <= 5; tier++) {
      const locked = tier > run.criminal.tierAccess;
      card.appendChild(el("h3", undefined, `Tier ${tier}: ${CRIMINAL_TIER_TITLES[tier - 1]}${locked ? "  🔒" : ""}`));
      for (const def of CRIMINAL_UPGRADES.filter((u) => u.tier === tier)) {
        const level = run.criminal.upgrades[def.id] ?? 0;
        const maxed = level >= def.maxLevel;
        const cost = criminalUpgradeCostAt(def, level);
        const row = el("div", "shop-row");
        row.appendChild(el("div", "shop-title", `${def.title} — Lv${level}/${def.maxLevel}`));
        row.appendChild(el("div", "shop-blurb", def.blurb));
        const buy = el("button", "shop-buy", maxed ? "MAX" : `Buy · $${cost.toLocaleString()}`);
        buy.type = "button";
        buy.disabled = locked || maxed || run.funding < cost;
        buy.addEventListener("click", () => {
          try {
            buyCriminalUpgrade(runtime.state, def.id);
            runtime.act([]); // persist the funding/level change
            render();
          } catch (err) {
            console.error("halfway: criminal buy failed", err);
          }
        });
        row.appendChild(buy);
        card.appendChild(row);
      }
    }

    const close = el("button", undefined, "Close");
    close.type = "button";
    close.addEventListener("click", () => sheet.remove());
    card.appendChild(close);
  };

  render();
  sheet.appendChild(card);
  sheet.addEventListener("click", (e) => {
    if (e.target === sheet) sheet.remove();
  });
  host.appendChild(sheet);
}

// ── per-scene action bars ───────────────────────────────────────────────────

const POLICY_ORDER: DischargePolicy[] = ["aggressive", "standard", "extended"];

interface FloatHost {
  floatText(x: number, y: number, msg: string, color: string): void;
}

export function installTierActions(overlay: OverlayHandles, tier: CareTierId, scene: FloatHost): void {
  overlay.setActiveTab(`tier-${tier}` as SceneKey);
  const specs: ActionSpec[] = [];

  if (tier === "detox") {
    specs.push({
      label: () => "Admit",
      icon: "assets/tiles/admit.webp",
      onTap: () => {
        const ev = admitFromQueue(runtime.state);
        if (ev.type === "admitBlocked") {
          scene.floatText(660, 1600, ev.reason === "noBed" ? "no open bed" : "queue is empty", "#9AA0A6");
        }
        runtime.act([ev]);
      },
    });
  }

  specs.push({
    label: () => {
      const lvl = runtime.state.run.tiers[tier].automatorLevel;
      const max = AUTOMATOR_ACTIONS_BY_LEVEL.length - 1;
      if (lvl >= max) return "Max";
      return lvl === 0
        ? `Hire $${automatorUpgradeCost(runtime.state, tier)}`
        : `Lv${lvl + 1} $${automatorUpgradeCost(runtime.state, tier)}`;
    },
    icon: "assets/tiles/staff.webp",
    secondary: true,
    disabled: () => {
      const max = AUTOMATOR_ACTIONS_BY_LEVEL.length - 1;
      return (
        runtime.state.run.tiers[tier].automatorLevel >= max ||
        runtime.state.run.funding < automatorUpgradeCost(runtime.state, tier)
      );
    },
    onTap: () => runtime.act([upgradeAutomator(runtime.state, tier)]),
  });

  specs.push({
    label: () => {
      const p = runtime.state.run.tiers[tier].dischargePolicy;
      return p.charAt(0).toUpperCase() + p.slice(1);
    },
    icon: "assets/tiles/policy.webp",
    secondary: true,
    onTap: () => {
      const cur = runtime.state.run.tiers[tier].dischargePolicy;
      const next = POLICY_ORDER[(POLICY_ORDER.indexOf(cur) + 1) % POLICY_ORDER.length]!;
      runtime.act([setDischargePolicy(runtime.state, tier, next)]);
    },
  });

  // v0.3: expansion PURCHASE (letter-gated three-layer model). The button
  // exists only on the frontier floor while an offer is open; guards
  // re-evaluate per refresh (the v0.2 stale-button lesson).
  if (frontierTier(runtime.state) === tier && runtime.state.run.purchasable !== null) {
    specs.push({
      label: () => {
        const offer = runtime.state.run.purchasable;
        if (offer === null) return "Closed";
        return `Open $${offer.price.toLocaleString()}`;
      },
      icon: "assets/tiles/open.webp",
      disabled: () => {
        const offer = runtime.state.run.purchasable;
        return offer === null || runtime.state.run.funding < offer.price;
      },
      onTap: () => {
        if (runtime.state.run.purchasable === null) return;
        runtime.act([purchaseTier(runtime.state)]);
      },
    });
  }

  overlay.setActions(specs);
}

export function installAlumniActions(overlay: OverlayHandles, scene: FloatHost): void {
  overlay.setActiveTab("alumni");
  overlay.setActions([
    {
      label: () => "Contact",
      icon: "assets/tiles/contact.webp",
      disabled: () => runtime.state.run.tick < runtime.state.run.alumni.contactBoostUntil,
      cooldown: () => {
        const remaining = runtime.state.run.alumni.contactBoostUntil - runtime.state.run.tick;
        return Math.max(0, Math.min(1, remaining / ALUMNI.contactCallBoostTicks));
      },
      onTap: () => {
        runtime.act([contactCall(runtime.state)]);
        scene.floatText(660, 1500, "referral boost!", "#3AA7A0");
      },
    },
    {
      label: () => {
        const lvl = runtime.state.run.alumni.coordinatorLevel;
        const max = AUTOMATOR_ACTIONS_BY_LEVEL.length - 1;
        if (lvl >= max) return "Max";
        return lvl === 0
          ? `Hire $${coordinatorUpgradeCost(runtime.state)}`
          : `Lv${lvl + 1} $${coordinatorUpgradeCost(runtime.state)}`;
      },
      icon: "assets/tiles/staff.webp",
      secondary: true,
      disabled: () => {
        const max = AUTOMATOR_ACTIONS_BY_LEVEL.length - 1;
        return (
          runtime.state.run.alumni.coordinatorLevel >= max ||
          runtime.state.run.funding < coordinatorUpgradeCost(runtime.state)
        );
      },
      onTap: () => runtime.act([upgradeCoordinator(runtime.state)]),
    },
  ]);
}

// ── the mailbox UI (v0.3) ──────────────────────────────────────────────────

function openMailSheet(onScene: (key: SceneKey) => void, handles: OverlayHandles): void {
  const host = root();
  if (host.querySelector(".sheet")) return;
  const sheet = el("div", "sheet");
  const card = el("div", "sheet-card");
  card.appendChild(el("h2", undefined, "Mail"));
  // Read letters LEAVE the inbox — they stay in state (the mailbox engine needs
  // them for arrival dedup) but the player's inbox shows only what's unread.
  const arrived = [...runtime.state.run.mailbox.arrived].reverse().filter((i) => !i.read);
  if (arrived.length === 0) {
    card.appendChild(el("div", "sheet-version", "no new mail — keep operating"));
  }
  for (const inst of arrived) {
    const letter = letterById(inst.id);
    const b = el("button", undefined, `✉ ${letter.title}`);
    b.type = "button";
    b.addEventListener("click", () => {
      sheet.remove();
      openLetterReader(inst.id, onScene, handles);
    });
    card.appendChild(b);
  }
  const close = el("button", undefined, "Close");
  close.type = "button";
  close.addEventListener("click", () => sheet.remove());
  card.appendChild(close);
  sheet.appendChild(card);
  sheet.addEventListener("click", (e) => {
    if (e.target === sheet) sheet.remove();
  });
  host.appendChild(sheet);
}

function openLetterReader(id: string, onScene: (key: SceneKey) => void, handles: OverlayHandles): void {
  const host = root();
  const letter = letterById(id);
  const instance = runtime.state.run.mailbox.arrived.find((i) => i.id === id);
  const sheet = el("div", "sheet");
  const card = el("div", "sheet-card letter");
  card.appendChild(el("h2", undefined, letter.title));
  for (const para of letter.body) {
    card.appendChild(el("p", undefined, para));
  }
  if (letter.finePrint) {
    card.appendChild(el("div", "fine-print", letter.finePrint));
  }
  if (instance && !instance.read) {
    // Reading commits a letter (optionally a chosen branch). A fail-loud throw
    // (e.g. can't afford SETTLE) leaves the sheet OPEN so the player can pick a
    // cheaper option; success removes it.
    const commit = (choiceIndex?: number): void => {
      try {
        const events = readLetter(runtime.state, id, choiceIndex);
        sheet.remove();
        for (const ev of events) {
          if (ev.type === "nameFacilityRequested") {
            const raw = window.prompt("Name your facility (something like a lakeside retreat):", DEFAULT_FACILITY_NAME);
            setFacilityName(runtime.state, raw ?? "");
          }
          if (ev.type === "teachControl" && (ev.control === "recruit" || ev.control === "admit")) {
            pendingPulse = ev.control;
          }
          if (ev.type === "prestigeOffered") {
            openPrestigeSheet(); // the letter routes straight into the sell screen
          }
          if (ev.type === "criminalUnlocked") {
            openCriminalLedger(); // the Business Proposition opens the back-office ledger
          }
        }
        runtime.act(events);
        if (letter.autoTravel) {
          onScene(letter.autoTravel as SceneKey);
        }
        handles.refresh();
      } catch (err) {
        console.error("halfway: letter failed", err); // sheet stays open on fail-loud
      }
    };
    if (letter.choices) {
      // A choice letter (the Death Letter): one button per branch, cost scaled
      // by the batched death count, disabled if unaffordable.
      const deaths = runtime.state.run.deathsPending;
      letter.choices.forEach((choice, i) => {
        const cost = choice.effects.reduce(
          (sum, e) => (e.type === "spendFundingPerDeath" ? sum + e.perDeath * deaths : sum),
          0,
        );
        const b = el("button", undefined, cost > 0 ? `${choice.label}  ($${cost.toLocaleString()})` : choice.label);
        b.type = "button";
        b.disabled = cost > runtime.state.run.funding;
        b.addEventListener("click", () => commit(i));
        card.appendChild(b);
      });
    } else {
      const openBtn = el("button", undefined, "FILE IT");
      openBtn.type = "button";
      openBtn.addEventListener("click", () => commit());
      card.appendChild(openBtn);
    }
  }
  const close = el("button", undefined, "Close");
  close.type = "button";
  close.addEventListener("click", () => sheet.remove());
  card.appendChild(close);
  sheet.appendChild(card);
  host.appendChild(sheet);
}

/** FTUE pulse (M13): the taught DOM control pulses until first use. */
let pendingPulse: "recruit" | "admit" | null = null;

export function applyPendingPulse(actionButtons: HTMLButtonElement[]): void {
  if (pendingPulse === null) return;
  const match = pendingPulse === "recruit" ? "RECRUIT" : "ADMIT";
  for (const b of actionButtons) {
    if (b.textContent?.toUpperCase().includes(match)) {
      b.classList.add("pulse");
      const clear = () => {
        b.classList.remove("pulse");
        b.removeEventListener("click", clear);
      };
      b.addEventListener("click", clear);
      pendingPulse = null;
      return;
    }
  }
}

/** Tagline for the current reputation (M12 ladder). */
export function taglineFor(rep: number): string {
  for (const rung of TAGLINES) {
    if (rep >= rung.minRep) return rung.text;
  }
  return TAGLINES[TAGLINES.length - 1]!.text;
}

/** Office action bar (labels module kept for OfficeScene compatibility). */
export const labels = {
  recruit: () => "Recruit",
  admit: () => "Admit",
  upgradeRecruiter: () => {
    const lvl = runtime.state.run.office.recruiterLevel;
    const max = OFFICE.recruiterIntervalByLevel.length - 1;
    if (lvl >= max) return "Max";
    return lvl === 0
      ? `Hire $${recruiterUpgradeCost(runtime.state)}`
      : `Lv${lvl + 1} $${recruiterUpgradeCost(runtime.state)}`;
  },
};

export { recruit, upgradeRecruiter, recruiterUpgradeCost, ALUMNI };

// ── transient widgets ───────────────────────────────────────────────────────

function humanDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

/**
 * "While you were away" — the offline report (v0.13.2). A DISMISSIBLE card,
 * not an auto-vanishing toast: the player reads what happened, and the
 * bed/queue lines expose WHERE offline income came from (diagnostics for
 * the manual-admit / bed-drain question). Off-tier income with empty beds
 * and no completions would show here as $ earned + 0 completions + 0 beds.
 */
export function showOfflineBanner(report: {
  ticks: number;
  awayMs?: number;
  fundingEarned: number;
  criminalEarned?: number;
  graduated: number;
  promoted?: number;
  died: number;
  relapsed: number;
  bedsOccupied?: number;
  queueWaiting?: number;
  heatHigh?: boolean;
  deaHigh?: boolean;
}): void {
  const host = root();
  if (host.querySelector(".sheet.offline-report")) return;
  const sheet = el("div", "sheet offline-report");
  const card = el("div", "sheet-card");
  // a11y (bot leg): a blocking modal — announced + focus-managed so
  // keyboard/screen-reader users aren't stranded (the old banner had
  // aria-live; the card must keep it).
  card.setAttribute("role", "dialog");
  card.setAttribute("aria-modal", "true");
  card.setAttribute("aria-live", "polite");
  card.setAttribute("aria-label", "While you were away");
  const away = report.awayMs !== undefined ? humanDuration(report.awayMs) : `${report.ticks.toLocaleString()} ticks`;
  card.appendChild(el("h2", undefined, "WHILE YOU WERE AWAY"));
  card.appendChild(el("div", "offline-sub", `You were gone ${away}. Here's what the facility did without you.`));

  const row = (label: string, value: string, warn = false): void => {
    const r = el("div", `offline-row${warn ? " warn" : ""}`);
    r.append(el("span", "offline-label", label), el("span", "offline-value", value));
    card.appendChild(r);
  };
  const completions = (report.graduated ?? 0) + (report.promoted ?? 0);
  // Signed: a fixer's offline bribes can outrun income → a real net loss.
  const net = report.fundingEarned;
  row("Net earned", `${net < 0 ? "−$" : "+$"}${Math.abs(net).toLocaleString()}`, net < 0);
  // v0.15 G5: break out "the other business" so criminal income isn't a
  // mystery lump — Russell asked to see it, and it explains money with no beds.
  // GROSS (not a subset of Net earned): with a fixer active, bribes make the
  // criminal net smaller than this, so it's labelled gross to avoid a child
  // line that appears to exceed its parent.
  if (report.criminalEarned !== undefined && report.criminalEarned > 0) {
    row("The other business (gross)", `+$${report.criminalEarned.toLocaleString()}`);
  }
  row("Completions", `${completions}${report.promoted !== undefined ? ` (${report.graduated} grad · ${report.promoted} promoted)` : ""}`);
  if (report.relapsed > 0) row("Relapsed", `${report.relapsed}`);
  if (report.died > 0) row("Died", `${report.died} ✝`, true);
  if (report.bedsOccupied !== undefined) row("Beds occupied now", `${report.bedsOccupied}`, report.bedsOccupied === 0);
  if (report.queueWaiting !== undefined) row("Leads waiting to admit", `${report.queueWaiting}`, report.queueWaiting > 0);
  if (report.heatHigh) row("Heat", "⚠ climbing", true);
  if (report.deaHigh) row("DEA", "⚠ climbing", true);

  // Diagnostic nudge: earned money but the beds are empty and nobody
  // completed — the money came from elsewhere (surfaces the reported bug).
  if (report.fundingEarned > 0 && completions === 0 && report.bedsOccupied === 0) {
    card.appendChild(el("div", "offline-diag", "Note: income accrued with no completions and no occupied beds."));
  }
  if (report.queueWaiting !== undefined && report.queueWaiting > 0 && report.bedsOccupied === 0) {
    card.appendChild(el("div", "offline-diag", "Your beds emptied while away — admit the waiting leads to restart the flow."));
  }

  const close = el("button", "prestige", "BACK TO WORK");
  close.type = "button";
  close.addEventListener("click", () => sheet.remove());
  card.appendChild(close);
  sheet.appendChild(card);
  host.appendChild(sheet);
  close.focus(); // move keyboard focus into the dialog (a11y, bot leg)
}

export function showSaveWarning(): void {
  const host = document.getElementById("ui-overlay");
  if (host === null) return;
  const note = el("div", "offline-banner", "⚠ SAVING FAILED — progress will not persist (storage full or private mode)");
  note.style.background = "var(--coral-red)";
  note.setAttribute("aria-live", "assertive");
  host.appendChild(note);
  window.setTimeout(() => note.remove(), 15000);
}

/**
 * The forced-prestige shutdown screen (v0.5): a meter game-over has already
 * reset the run under the hood. Freeze the runtime, persist the fresh run, and
 * offer a REOPEN (reload) — the same never-a-wipe shape as voluntary prestige.
 */
const SHUTDOWN_COPY: Record<GameOverReason, { title: string; body: string }> = {
  shutdown: {
    title: "SHUT DOWN",
    body: "The state has suspended your license. Inspectors are removing the beds — a buyer was, conveniently, already arranged.",
  },
  collapse: {
    title: "REPUTATION COLLAPSED",
    body: "Your name is worth less than nothing. The board replaced you and rebranded overnight; the sign is already different.",
  },
  raid: {
    title: "THE RAID",
    body: "Federal agents came through the front doors at dawn — and the records room after that. The officials you paid did not return your calls. What could be seized was seized.",
  },
};

export function showShutdownScreen(reason: GameOverReason, payout: number): void {
  const host = root();
  host.querySelector(".sheet")?.remove(); // a shutdown supersedes any open sheet
  hideEventCard(); // a game-over supersedes any open card — stop its RAF timer (bot leg)
  runtime.stop(); // freeze the fresh run until REOPEN reloads
  try {
    saveGame(runtime.state);
  } catch (err) {
    console.error("halfway: shutdown save failed", err);
    showSaveWarning();
  }
  const sheet = el("div", "sheet");
  const card = el("div", "sheet-card prestige-sheet");
  const copy = SHUTDOWN_COPY[reason];
  card.appendChild(el("h2", undefined, copy.title));
  card.appendChild(el("p", undefined, copy.body));
  const line = el("div", "prestige-payout");
  line.append(el("b", undefined, `+${payout.toLocaleString()} RC`), " salvaged (a forced sale pays less)");
  card.appendChild(line);
  const reopen = el("button", "prestige", "REOPEN UNDER NEW MANAGEMENT");
  reopen.type = "button";
  reopen.addEventListener("click", () => window.location.reload());
  card.appendChild(reopen);
  sheet.appendChild(card);
  host.appendChild(sheet);
}

// ── v0.12 The Grievance Box ─────────────────────────────────────────────────

const GRIEVANCE_HELP: Record<FeedbackCategory, string> = {
  // RULED §8.2: the directions are GENUINE and instructive — they teach,
  // they don't joke. The skin is satirical; the help text plays it straight.
  bug: "Tell us: what were you doing, what did you expect, and what happened instead? If you can, attach your save below — it makes the fix much faster.",
  idea: "Pitch a joke or an improvement. Where does it live (which room or screen), and what does it say or do? Specific beats subtle — write the line the way you'd want to see it.",
};

/** One grievance per 30s after a success (we're the easiest griefers of our own inbox). */
let lastGrievanceSentAt = 0;

function openGrievanceSheet(): void {
  const host = root();
  if (host.querySelector(".sheet")) return;
  const sheet = el("div", "sheet");
  const card = el("div", "sheet-card grievance");
  card.appendChild(el("h2", undefined, "GRIEVANCE FORM 12-B"));
  card.appendChild(el("div", "grievance-intro", "Goes straight to the developer. Real inbox, real reads."));

  let category: FeedbackCategory = "bug";
  const bugBtn = el("button", "toggle active", "BUG");
  const ideaBtn = el("button", "toggle", "IDEA");
  bugBtn.type = "button";
  ideaBtn.type = "button";
  const toggleRow = el("div", "grievance-toggle");
  toggleRow.append(bugBtn, ideaBtn);
  card.appendChild(toggleRow);
  const helper = el("div", "grievance-helper", GRIEVANCE_HELP.bug);
  card.appendChild(helper);
  const pick = (cat: FeedbackCategory): void => {
    category = cat;
    bugBtn.className = cat === "bug" ? "toggle active" : "toggle";
    ideaBtn.className = cat === "idea" ? "toggle active" : "toggle";
    helper.textContent = GRIEVANCE_HELP[cat];
  };
  bugBtn.addEventListener("click", () => pick("bug"));
  ideaBtn.addEventListener("click", () => pick("idea"));

  const ta = document.createElement("textarea");
  ta.maxLength = FEEDBACK_TEXT_MAX;
  ta.rows = 5;
  ta.placeholder = "Write it here…";
  card.appendChild(ta);

  const attachRow = el("label", "grievance-attach");
  const attach = document.createElement("input");
  attach.type = "checkbox";
  const attachText = el("span", undefined, " attach my save file (helps reproduce bugs)");
  attachRow.append(attach, attachText);
  card.appendChild(attachRow);

  const disclosure = el("div", "grievance-disclosure", "sends your note, game version, device type, and run progress");
  attach.addEventListener("change", () => {
    disclosure.textContent = attach.checked
      ? "sends your note, game version, device type, run progress, and your save"
      : "sends your note, game version, device type, and run progress";
  });
  card.appendChild(disclosure);

  const errLine = el("div", "grievance-error", "");
  errLine.style.display = "none";
  card.appendChild(errLine);

  const send = el("button", "prestige", "SEND");
  send.type = "button";
  send.disabled = true;
  let inFlight = false;
  const refreshSend = (): void => {
    const coolingDown = Date.now() - lastGrievanceSentAt < 30_000;
    send.disabled = inFlight || coolingDown || ta.value.trim().length === 0;
  };
  ta.addEventListener("input", refreshSend);
  refreshSend();
  const coolRemaining = 30_000 - (Date.now() - lastGrievanceSentAt);
  if (coolRemaining > 0) {
    errLine.textContent = "one grievance per 30 seconds — give the form a moment";
    errLine.style.display = "block";
    // Re-arm when the cooldown lapses — input events alone would leave
    // SEND disabled forever for a patient typist (v0.12 review, swarm).
    window.setTimeout(() => {
      refreshSend();
      if (Date.now() - lastGrievanceSentAt >= 30_000) errLine.style.display = "none";
    }, coolRemaining + 50);
  }

  send.addEventListener("click", () => {
    inFlight = true;
    refreshSend();
    errLine.style.display = "none";
    sendFeedback(runtime.state, category, ta.value, attach.checked)
      .then((res) => {
        lastGrievanceSentAt = Date.now();
        card.textContent = "";
        card.appendChild(el("h2", undefined, "GRIEVANCE FILED."));
        card.appendChild(el("div", "grievance-intro", "It will be read. This is a real promise, not a laminated one."));
        if (res.sent && res.saveTooLarge) {
          card.appendChild(el("div", "grievance-disclosure", "(your save was too large to attach — the note still went through)"));
        }
        if (!res.sent) {
          card.appendChild(el("div", "grievance-disclosure", "dev build — logged to console, not sent"));
        }
        const done = el("button", undefined, "Close");
        done.type = "button";
        done.addEventListener("click", () => sheet.remove());
        card.appendChild(done);
      })
      .catch(() => {
        // Fail-loud, text KEPT (RFC G3) — never silently eat a typed report.
        inFlight = false;
        refreshSend();
        errLine.textContent = "couldn't send — check your connection and try again";
        errLine.style.display = "block";
      });
  });
  card.appendChild(send);

  const close = el("button", undefined, "Close");
  close.type = "button";
  close.addEventListener("click", () => {
    // Closing mid-send would detach the failure path's error line and
    // lose the typed text (RFC G3) — hold the door until it settles.
    if (inFlight) return;
    sheet.remove();
  });
  card.appendChild(close);

  sheet.appendChild(card);
  host.appendChild(sheet);
}

// ── 0.12.2 the consent form (playtest disclaimer) ──────────────────────────

const CONSENT_KEY = "halfway.consent.v1";

/** Once per BROWSER (own key, outside the save — New Game must not
 * re-show it; a cleared browser is a new playtester). */
export function consentGiven(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) === "signed";
  } catch {
    // No storage = the signature can't persist → SHOW the form each boot.
    // A disclaimer that hides from private-browsing users is backwards
    // (bot leg: the old `true` here contradicted exactly that intent).
    return false;
  }
}

/** The opening disclaimer, skinned as facility paperwork (LEGIBILITY LAW:
 * the content plays it straight — satire aim, playtest state, telemetry,
 * and the real helpline). Blocks nothing but sight; one button. */
export function showConsentForm(onSigned?: () => void): void {
  const host = root();
  const sheet = el("div", "sheet consent");
  const card = el("div", "sheet-card grievance");
  card.appendChild(el("h2", undefined, "INFORMED CONSENT — FORM 0-A"));
  const para = (head: string, body: string): void => {
    const p = el("div", "consent-para");
    p.appendChild(el("strong", undefined, head + " "));
    p.appendChild(document.createTextNode(body));
    card.appendChild(p);
  };
  para("This game is satire.", "Rehab Tycoon is dark comedy about the BUSINESS of recovery — the incentives, the paperwork, the billing codes. The jokes aim at the industry, never at people in recovery.");
  para("This is an early playtest.", "Expect rough edges, strange balance, and honest bugs. Updates may reset your save. Numbers are placeholders wearing lab coats.");
  para("This build reports home.", "Anonymous usage, error and progress reports only — no accounts, no cookies, no personal details, just counts and a random install ID. Found a bug? Have a better joke? Menu → File a Grievance. It gets read.");
  para("And for real:", "if you or someone you care about is struggling with addiction, SAMHSA's free, confidential, 24/7 helpline is 1-800-662-4357.");
  const sign = el("button", "prestige", "I CONSENT TO TREATMENT");
  sign.type = "button";
  sign.addEventListener("click", () => {
    try {
      localStorage.setItem(CONSENT_KEY, "signed");
    } catch {
      /* no storage — the form simply shows again next boot */
    }
    sheet.remove();
    onSigned?.(); // e.g. analytics loads only AFTER the paperwork (bot leg)
  });
  card.appendChild(sign);
  sheet.appendChild(card);
  host.appendChild(sheet);
}

// ── v0.14 the event card (RFC §3.3) ────────────────────────────────────────

let eventCardEl: HTMLElement | null = null;
let eventCardId: number | null = null;
let eventCardRAF: number | undefined;

/** THE event card — big, front and center, timer draining, choices. While
 * it's up the sim keeps freezing its scene (engine side). One at a time;
 * a queued card surfaces when this resolves (main.ts re-checks state). */
export function showEventCard(
  card: { id: number; title: string; body: string; crisis: boolean; choices: string[]; lifetimeTicks: number; getTicksLeft: () => number; hero?: string },
  onChoose: (choiceIndex: number) => void,
): void {
  if (eventCardId === card.id) return; // already showing it
  hideEventCard();
  const sheet = el("div", "sheet event-card-sheet");
  const cardEl = el("div", `sheet-card event-card${card.crisis ? " crisis" : ""}`);
  // v0.14.1: full-bleed hero banner at the top of the card. Decorative — a
  // missing file hides the img and the card still works (art-layer contract).
  if (card.hero) {
    const hero = el("img", "event-hero") as HTMLImageElement;
    hero.src = assetUrl(card.hero);
    hero.alt = "";
    hero.addEventListener("error", () => {
      // Log the URL before hiding so a broken asset is debuggable, not a silent
      // no-art card (matches the art-pack fallback log). The card still works.
      console.warn(`[card] hero art failed to load, hiding it: ${hero.src}`);
      hero.remove();
    });
    cardEl.appendChild(hero);
  }
  const bar = el("div", "event-timer");
  const fill = el("i");
  bar.appendChild(fill);
  cardEl.appendChild(bar);
  if (card.crisis) cardEl.appendChild(el("div", "event-crisis-flag", "CRISIS — every second, someone gets worse"));
  cardEl.appendChild(el("h2", undefined, card.title));
  cardEl.appendChild(el("div", "event-body", card.body));
  card.choices.forEach((label, i) => {
    const btn = el("button", "event-choice", label);
    btn.type = "button";
    btn.addEventListener("click", () => onChoose(i));
    cardEl.appendChild(btn);
  });
  sheet.appendChild(cardEl);
  root().appendChild(sheet);
  eventCardEl = sheet;
  eventCardId = card.id;
  // Timer bar tracks LIVE ticksLeft via a per-frame read of the getter.
  const tick = (): void => {
    if (eventCardId !== card.id) return;
    const left = card.getTicksLeft();
    const frac = Math.max(0, Math.min(1, left / card.lifetimeTicks));
    fill.style.width = `${frac * 100}%`;
    eventCardRAF = window.requestAnimationFrame(tick);
  };
  eventCardRAF = window.requestAnimationFrame(tick);
}

export function hideEventCard(): void {
  if (eventCardRAF !== undefined) window.cancelAnimationFrame(eventCardRAF);
  eventCardRAF = undefined;
  eventCardEl?.remove();
  eventCardEl = null;
  eventCardId = null;
}

/** The id of the card currently on screen (main.ts reconciles against state). */
export function shownEventCardId(): number | null {
  return eventCardId;
}

// ── v0.13 the satire toast (RFC §3.4) ──────────────────────────────────────

let satireToast: HTMLElement | null = null;
let satireTimer: number | undefined;

/** One at a time; a new toast replaces the old. textContent-only. */
export function showSatireToast(text: string): void {
  satireToast?.remove();
  window.clearTimeout(satireTimer);
  const toast = el("div", "satire-toast", text);
  toast.setAttribute("role", "status");
  root().appendChild(toast);
  satireToast = toast;
  satireTimer = window.setTimeout(() => {
    toast.classList.add("gone");
    window.setTimeout(() => {
      toast.remove();
      if (satireToast === toast) satireToast = null;
    }, 500);
  }, 6000);
}

/**
 * The pack-load veil (v0.11 §3.2): shown when a tier is entered before its
 * art pack landed. Singleton — repeat shows reuse the element; hide is
 * idempotent. The joke is the wait itself: loading as prior authorization.
 */
let insuranceVeil: HTMLElement | null = null;

export function showInsuranceVeil(): void {
  if (insuranceVeil !== null) return;
  const veil = el("div", "insurance-veil");
  veil.setAttribute("role", "status");
  veil.append(el("div", "veil-spinner"), el("div", "veil-line", "VERIFYING INSURANCE…"));
  root().appendChild(veil);
  insuranceVeil = veil;
}

export function hideInsuranceVeil(): void {
  insuranceVeil?.remove();
  insuranceVeil = null;
}

export function showFatalPanel(message: string, onNewGame: () => void): void {
  const host = root();
  const panel = el("div", "fatal");
  panel.setAttribute("role", "alert");
  panel.append(
    el("div", undefined, "SAVE ERROR"),
    el("div", undefined, message),
    el("div", undefined, "Your save was NOT modified. Starting a new game will erase it."),
  );
  const btn = el("button", undefined, "NEW GAME (erase save)");
  btn.type = "button";
  btn.addEventListener("click", onNewGame);
  panel.appendChild(btn);
  host.appendChild(panel);
}

/**
 * Tap-a-prop gag modal (v0.9 ruling 8): the close-up joke for a curated prop.
 * Sheet-pattern, textContent-only (XSS-safe like every overlay surface).
 */
export function showGagModal(title: string, lines: string[]): void {
  const host = root();
  if (host.querySelector(".sheet")) return; // one sheet at a time
  const sheet = el("div", "sheet");
  const card = el("div", "sheet-card");
  card.appendChild(el("h2", undefined, title));
  for (const line of lines) card.appendChild(el("p", undefined, line));
  const close = el("button", "secondary", "Close");
  close.type = "button";
  close.addEventListener("click", () => sheet.remove());
  card.appendChild(close);
  sheet.appendChild(card);
  sheet.addEventListener("click", (e) => {
    if (e.target === sheet) sheet.remove();
  });
  // Escape closes here too (CodeRabbit, PR #20 — the earlier Escape fix
  // pattern-matched the More sheet; this is the gag modal's own).
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") sheet.remove();
  };
  document.addEventListener("keydown", onKey);
  const obs = new MutationObserver(() => {
    if (!sheet.isConnected) {
      document.removeEventListener("keydown", onKey);
      obs.disconnect();
    }
  });
  obs.observe(host, { childList: true });
  host.appendChild(sheet);
}
